import { slugify } from '@scalar-org/helpers/url'
import { cancel, confirm, isCancel, text } from '@clack/prompts'

import fs from 'node:fs'
import path from 'node:path'
import { Command } from 'commander'
import as from 'ansis'

import { CONFIG_FILE_DEFAULT } from '../helpers'
import { output } from '@/helpers/std-out'

/**
 * Scalar configuration file (scalar.config.json)
 */
export type ScalarConfigurationFile = {
  subdomain: string
  references: ScalarReferenceEntry[]
  guides: ScalarGuideEntry[]
}

/**
 * Entry for an API reference
 */
export type ScalarReferenceEntry = {
  name: string
  path: string
}

/**
 * Entry for the guide
 */
export type ScalarGuideEntry = {
  name: string
  sidebar: ScalarSidebarEntry[]
}

/**
 * Entry for the sidebar (folder or page)
 */
export type ScalarSidebarEntry = {
  path?: string
  type: 'folder' | 'page'
  items?: ScalarSidebarEntry[]
}

export const InitCommand = () => {
  const cmd = new Command('init')

  cmd.description(
    'Create a new `scalar.config.json` file to configure where your OpenAPI file is placed.',
  )
  cmd.option('-f, --file [file]', 'your OpenAPI file')
  cmd.option('-s, --subdomain [url]', 'subdomain to publish on')
  cmd.option('--force', 'override existing configuration')
  cmd.action(async ({ file, subdomain, force }) => {
    // Path to `scalar.config.json` file
    const configFile = path.resolve(CONFIG_FILE_DEFAULT)
    let validInput: boolean
    let input = file

    const nextSteps = () => {
      output
        .info()
        .line('What to do next:')
        .line(
          `  ${as.cyan('scalar document format')} ${as.gray('[options] [file|url]')} to format your OpenAPI file`,
        )
        .line(
          `  ${as.cyan('scalar document validate')} ${as.gray('[file|url]')} to validate your OpenAPI file`,
        )
        .line(
          `  ${as.cyan('scalar document bundle')} ${as.gray('[options] [file]')} to bundle your OpenAPI file`,
        )
        .line(
          `  ${as.cyan('scalar document serve')} ${as.gray('[options] [file|url]')} to serve your OpenAPI file \n`,
        )
        .line(
          as.white(
            `Run ${as.magenta('scalar --help')} to see all available commands.`,
          ),
        )
        .print()
    }

    // Handle cancel from the user
    const handleCancel = () => {
      cancel('Operation cancelled.')
      nextSteps()
      process.exit(0)
    }

    // Function to validate file extension
    function isValidFile(filePath: string) {
      const validExtensions = ['.json', '.yaml', '.yml']
      const extension = path.extname(filePath).toLowerCase()
      return validExtensions.includes(extension)
    }

    // Check if `scalar.config.json` already exists
    if (fs.existsSync(configFile)) {
      output
        .info()
        .line(
          `${as.green('⚠')} Found existing configuration: ${as.green.bold(`${CONFIG_FILE_DEFAULT}`)}`,
        )
        .print()

      if (force) {
        output
          .info()
          .line(`${as.green('✔')} Overwriting existing file…`)
          .print()
      }

      const shouldOverwriteExisting =
        force ??
        (await confirm({
          message: 'Do you want to override the file?',
          initialValue: false,
        }))

      if (isCancel(shouldOverwriteExisting)) {
        handleCancel()
      }

      if (!shouldOverwriteExisting) {
        handleCancel()
      }
    }

    // New configuration object
    const configuration: ScalarConfigurationFile = {
      subdomain: '',
      references: [],
      guides: [],
    }

    // Subdomain
    validInput = !!subdomain

    while (!validInput) {
      const response = await text({
        message:
          'What is the name of your project? We will use that to create a custom subdomain for you.',
        validate(value: string) {
          if (value.trim().length === 0) {
            return 'You did not provide a project name. Please provide a name!'
          }

          return
        },
      })

      // TODO: Check if the subdomain is available
      if (isCancel(response)) {
        handleCancel()
      } else {
        validInput = true
      }

      const slug = slugify(response.toString())

      subdomain = `${slug}.apidocumentation.com`

      output
        .info()
        .line(`${as.green('✔')} Subdomain: ${as.green(subdomain)}`)
        .print()
    }

    configuration.subdomain = subdomain.trim()

    // Reference

    // Check if the file option is provided and valid
    if (input) {
      const validExtensions = ['.json', '.yaml', '.yml']
      const extension = path.extname(input).toLowerCase()
      if (!validExtensions.includes(extension)) {
        output
          .error()
          .message(
            `✖ Please enter a valid file path ${validExtensions.join(', ')}.`,
          )
          .print()
      }
    }

    // Ask for the file path
    validInput = input && isValidFile(input)

    while (!validInput) {
      const response = await text({
        message: `Where is your OpenAPI file? ${as.grey('(Add a path to the file)')}`,
        validate(value: string) {
          if (value.length === 0) {
            return 'Value is required!'
          }

          return
        },
      })

      input = response

      if (isCancel(response)) {
        handleCancel()
      }

      if (isValidFile(input)) {
        validInput = true
      } else {
        output
          .error()
          .message(
            `✖ Invalid file extension. Expected: ${['.json', '.yaml', '.yml'].join(', ')}.`,
          )
          .print()
      }
    }

    configuration.references.push({
      name: 'API Reference',
      path: input,
    })

    const content = JSON.stringify(configuration, null, 2)

    // Create `scalar.config.json` file
    fs.writeFileSync(configFile, content)

    output
      .info()
      .line(`${as.green('✔')} Configuration stored. \n`)
      .line(`${as.green.bold(`${CONFIG_FILE_DEFAULT}`)} \n`)
      .line(
        `${as.grey(
          content
            .split('\n')
            .map((line) => `  ${line}`)
            .join('\n'),
        )}`,
      )
      .print()
    nextSteps()
  })

  return cmd
}
